// SSG API Client - Secure Backend Implementation
// Supports OAuth 2.0, Certificate (.pem), and PKCS12 (.p12) authentication
// Credentials are stored securely in Supabase Edge Function environment variables

class SSGAPIClient {
  constructor() {
    this.baseUrl = 'https://public-api.ssg-wsg.sg';
    this.certBaseUrl = 'https://api.ssg-wsg.sg';
    this.supabase = null; // Will be set by the component
  }

  // Set Supabase client (called from component)
  setSupabaseClient(supabaseClient) {
    this.supabase = supabaseClient;
  }

  // Generic request method using secure backend
  async makeRequest(endpoint, method = 'POST', body = null, authType = 'oauth') {
    if (!this.supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      console.log(`Making ${authType} ${method} request to: ${endpoint}`);
      if (body) {
        console.log('Body:', JSON.stringify(body, null, 2));
      }

      const { data, error } = await this.supabase.functions.invoke("ssg-api", {
        body: { 
          apiEndpoint: endpoint,
          method: method,
          requestData: body,
          authType: authType
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      console.log('Response:', data);
      return data;
    } catch (error) {
      console.error('Request failed:', error);
      throw new Error(`SSG API request failed: ${error.message}`);
    }
  }

  // OAuth-based API methods
  async createClaim(claimData) {
    const endpoint = 'skillsFutureCredits/claims/createClaim';
    return await this.makeRequest(endpoint, 'POST', claimData, 'oauth');
  }

  async encryptRequest(encryptedData) {
    const endpoint = 'skillsFutureCredits/claims/encryptRequests';
    return await this.makeRequest(endpoint, 'POST', encryptedData, 'oauth');
  }

  async submitClaim(claimData) {
    const endpoint = 'skillsFutureCredits/claims/submitClaim';
    return await this.makeRequest(endpoint, 'POST', claimData, 'oauth');
  }

  async cancelClaim(claimId, cancelData) {
    const endpoint = `skillsFutureCredits/claims/${claimId}/cancelClaim`;
    return await this.makeRequest(endpoint, 'POST', cancelData, 'oauth');
  }

  async getClaimStatus(claimId) {
    const endpoint = `skillsFutureCredits/claims/${claimId}/status`;
    return await this.makeRequest(endpoint, 'GET', null, 'oauth');
  }

  async calculateGrant(grantData) {
    const endpoint = 'skillsFutureCredits/grantCalculator';
    return await this.makeRequest(endpoint, 'POST', grantData, 'oauth');
  }

  async searchCourses(searchParams) {
    const endpoint = 'skillsFutureCredits/courses/search';
    return await this.makeRequest(endpoint, 'POST', searchParams, 'oauth');
  }

  async getCourseDetails(courseId) {
    const endpoint = `skillsFutureCredits/courses/${courseId}`;
    return await this.makeRequest(endpoint, 'GET', null, 'oauth');
  }

  // Certificate-based API methods (using .pem files)
  async getSectors() {
    const endpoint = '/skillsFramework/sectors';
    return await this.makeRequest(endpoint, 'GET', null, 'cert');
  }

  async getOccupations(sectorId) {
    const endpoint = `/skillsFramework/sectors/${sectorId}/occupations`;
    return await this.makeRequest(endpoint, 'GET', null, 'cert');
  }

  async getSkills(occupationId) {
    const endpoint = `/skillsFramework/occupations/${occupationId}/skills`;
    return await this.makeRequest(endpoint, 'GET', null, 'cert');
  }

  // P12-based API methods (using .p12 file)
  async getSectorsP12() {
    const endpoint = '/skillsFramework/sectors';
    return await this.makeRequest(endpoint, 'GET', null, 'p12');
  }

  async getOccupationsP12(sectorId) {
    const endpoint = `/skillsFramework/sectors/${sectorId}/occupations`;
    return await this.makeRequest(endpoint, 'GET', null, 'p12');
  }

  async getSkillsP12(occupationId) {
    const endpoint = `/skillsFramework/occupations/${occupationId}/skills`;
    return await this.makeRequest(endpoint, 'GET', null, 'p12');
  }

  // Test connection for different auth types
  async testConnection(authType = 'oauth') {
    try {
      let result;
      if (authType === 'oauth') {
        // Try to get a simple endpoint to test OAuth connection
        result = await this.getClaimStatus('test');
      } else if (authType === 'cert') {
        // Try to get sectors to test certificate connection
        result = await this.getSectors();
      } else if (authType === 'p12') {
        // Try to get sectors to test P12 connection
        result = await this.getSectorsP12();
      }
      
      return {
        success: true,
        message: `${authType.toUpperCase()} connection successful`,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        message: error.message
      };
    }
  }
}

// API Configuration
const API_CONFIG = {
  PROD_URL: 'https://public-api.ssg-wsg.sg',
  CERT_PROD_URL: 'https://api.ssg-wsg.sg',
  DEV_URL: 'https://dev-api.ssg-wsg.sg', // if you have a dev environment
  TEST_URL: 'https://test-api.ssg-wsg.sg' // if you have a test environment
};

// Data Models for SSG API requests
export class ClaimRequest {
  constructor(data) {
    this.claimRequest = {
      course: {
        id: data.courseId,
        fee: data.courseFee,
        runId: data.courseRunId || "",
        startDate: data.startDate
      },
      individual: {
        nric: data.nric,
        email: data.email,
        homeNumber: data.homeNumber || data.mobileNumber,
        mobileNumber: data.mobileNumber
      },
      additionalInformation: data.additionalInformation || ""
    };
  }

  toJSON() {
    return this.claimRequest;
  }
}

export class GrantCalculatorRequest {
  constructor(data) {
    this.course = {
      referenceNumber: data.courseReferenceNumber,
      startDate: data.startDate
    };
    this.trainee = {
      idType: data.idType || "NRIC",
      id: data.id,
      dateOfBirth: data.dateOfBirth,
      sponsoringEmployerUen: data.sponsoringEmployerUen || ""
    };
  }

  toJSON() {
    return {
      course: this.course,
      trainee: this.trainee
    };
  }
}

export class CancelClaimRequest {
  constructor(reason) {
    this.cancelReason = reason;
  }

  toJSON() {
    return {
      cancelReason: this.cancelReason
    };
  }
}

// API Response Models
export class SSGAPIResponse {
  constructor(response) {
    this.status = response.status;
    this.statusText = response.statusText;
    this.data = response.data;
    this.headers = response.headers;
    this.url = response.url;
    this.ok = response.ok;
  }

  isSuccess() {
    return this.ok && this.status >= 200 && this.status < 300;
  }

  getErrorMessage() {
    if (this.data && this.data.error) {
      return this.data.error;
    }
    if (this.data && this.data.message) {
      return this.data.message;
    }
    return this.statusText || 'Unknown error occurred';
  }
}

// Factory for creating API requests
export class SSGAPIRequestFactory {
  static createClaimRequest(data) {
    return new ClaimRequest(data);
  }

  static createGrantCalculatorRequest(data) {
    return new GrantCalculatorRequest(data);
  }

  static createCancelClaimRequest(reason) {
    return new CancelClaimRequest(reason);
  }
}

// Export the main client
export const ssgApiClient = new SSGAPIClient();

// Export individual methods for convenience
export const {
  createClaim,
  encryptRequest,
  submitClaim,
  cancelClaim,
  getClaimStatus,
  calculateGrant,
  searchCourses,
  getCourseDetails,
  getSectors,
  getOccupations,
  getSkills,
  getSectorsP12,
  getOccupationsP12,
  getSkillsP12,
  testConnection
} = ssgApiClient; 